import {
  BarChart3,
  Boxes,
  CalendarDays,
  Image as ImageIcon,
  Layers,
  LayoutTemplate,
  Megaphone,
  Store,
  Smartphone,
  Ticket,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

import type { AdminRole } from '../auth/roles';

/**
 * The module-mount contract (admin-shell-oidc-rbac B6). A module subject
 * (admin-marketing-module, admin-platform-module, …) contributes ONE entry: a
 * route path, a nav label, an icon, the minimum role tier(s), the sub-sections
 * the rail should expose, and a lazily-loaded page component. The shell renders
 * nav + routes from this registry, so modules ship without editing the shell.
 * Keep entries here additive.
 */

/**
 * One sub-section of a module, shown INDENTED under it in the rail (L8 round 11,
 * items 7+9 — Play's wp-admin submenu model, replacing the in-page tab bars).
 *
 * ⚠ Nav metadata ONLY. Routing is unchanged: `App.tsx` still mounts exactly one
 * `<Route path={routePattern}>` per module and each module owns its internal
 * `<Routes>`, so adding/removing a child never moves a URL. Do NOT try to
 * generate routes from these — the flat `.map()` in `App.tsx` would silently
 * render nothing.
 */
export interface NavChild {
  /** Path segment relative to the module's `path`; `''` is the module index. */
  readonly path: string;
  readonly navLabel: string;
  /**
   * Narrower than the module's own gate, when a sub-page needs it. Defaults to
   * the module's `requiredRoles`. (Reserved for the `support` role flagged in
   * ../outbound-chat/AGENTS.md — that page is nested under Marketing now, so a
   * support-only user would otherwise need the whole Marketing tier.)
   */
  readonly requiredRoles?: readonly AdminRole[];
  /**
   * Short dimmed suffix in the rail — today only `'PH2'`, marking a section
   * that exists as a signpost while its feature is deferred. The page behind
   * it must explain the same thing; the marker alone is not an explanation.
   */
  readonly note?: string;
}

export interface ModuleMount {
  /** The nav-link target (the module's index path). */
  readonly path: string;
  /**
   * Group heading rendered ABOVE this entry in the rail, once per run of
   * consecutive modules sharing the value (the BO prototype's "NEW SHOP" band;
   * "NEW OFFERS" over the offer-system modules). It marks areas gigantica adds
   * on top of Play's wp-admin. Modules without it render ungrouped, exactly as
   * before — so every module of a band carries the value, not just the first.
   */
  readonly section?: string;
  /**
   * Same meaning as `NavChild.note` — a deferred-phase marker (`'PH2'`). The
   * whole row is tinted, because the section itself is the thing that is not
   * ready, not one sub-page of it.
   */
  readonly note?: string;
  /**
   * The route-matching pattern for `<Route path>`. Defaults to `path`. A module
   * with internal sub-pages sets a trailing-wildcard pattern (e.g. `/platform/*`)
   * so React Router matches its nested routes, while `path` stays the clean nav link.
   */
  readonly routePattern?: string;
  readonly navLabel: string;
  /**
   * Rail glyph. `lucide-react` component imports (NOT `ui/lucide-glyph`, which
   * reads the whole bundled `icon-nodes.json` — fine behind the lazy outbound-chat
   * chunk, but it would drag the entire catalog into the ENTRY bundle from here).
   * Play's sidebar is fully iconed, so every entry carries one.
   *
   * Typed as lucide's own `LucideIcon`, not a structural
   * `ComponentType<{size?, className?}>`: lucide's `size` is `string | number`
   * and its `propTypes` variance makes the narrower structural type reject every
   * icon. Coupling the field to lucide is honest — these ARE lucide components.
   */
  readonly icon?: LucideIcon;
  /** Any-of (hierarchy-aware). `['operator']` ⇒ operator OR admin. */
  readonly requiredRoles: readonly AdminRole[];
  /** Sub-sections shown indented in the rail when this module is active. */
  readonly children?: readonly NavChild[];
  readonly component: LazyExoticComponent<ComponentType>;
}

/**
 * ⚠ LD-9's "FOCUS modules only" rule is SUPERSEDED for two entries (ADR-028).
 *
 * It used to read: *NO "Apps" (the app-instance structure is dropped);
 * Scheduler/Leaderboard are NOT generic nav (reshaped into their domain
 * subjects). Do not add generic nav for them.* That held while gigantica was
 * only ever going to serve the leaderboard slice of Play's apps framework.
 *
 * The cutover changed the requirement: the admin SPA has to REPLACE wp-admin,
 * and Play's operators use both `admin.php?page=apps` and
 * `admin.php?page=promo-scheduler` daily. So `Apps` and `Scheduler` are back as
 * top-level mounts, at Play's own relative positions. What stays retired is the
 * *generic framework* — there is no `sc_apps` table here; Apps reads the typed
 * registries (`platform.feature_configs` + `platform.app_instances`) and
 * Scheduler reads real `tournament.schedules` rows.
 *
 * `Leaderboard` is untouched by this: it remains its own domain subject, and it
 * LOST its `Schedules` child to the new Scheduler mount (one place for the rule).
 */
export const moduleRegistry: readonly ModuleMount[] = [
  {
    // admin-marketing-module B1: the sc-marketing port — Segments, Messages, etc.
    // One mount with internal sub-routes (deep-linkable), so it needs a wildcard
    // routePattern (mirrors the platform module).
    path: '/marketing',
    routePattern: '/marketing/*',
    // Play's menu is plain "Marketing" (sc-marketing/src/MenuRegistrar.php:25-33).
    // The old "(Popcorn)" suffix was ours, for wp-admin familiarity; round 11
    // dropped it since the Popcorn pages now name themselves in the rail below.
    navLabel: 'Marketing',
    icon: Megaphone, // Play: dashicons-megaphone
    requiredRoles: ['operator'],
    // ORDER and LABELS mirror Play's marketing SPA nav byte-for-byte
    // (sc-marketing/src/app/routes.tsx, re-verified 2026-09-15 for RND-2260):
    // Users, Segments, Popcorn Planner, Popcorn Messages, Popcorn Placeholders,
    // Captain Up, Banners Logged-out, Games, LogicX Logs, Outbound Chat. The two
    // gigantica-only sections (Dynamic Templates, Step Presets — Play reaches
    // these from inside its message editor, and omits them from its own nav) are
    // APPENDED rather than interleaved, so Play's relative order is preserved.
    children: [
      { path: 'users', navLabel: 'Users' },
      { path: 'segments', navLabel: 'Segments' },
      { path: 'slots', navLabel: 'Popcorn Planner' },
      { path: 'messages', navLabel: 'Popcorn Messages' },
      { path: 'placeholders', navLabel: 'Popcorn Placeholders' },
      { path: 'captainup', navLabel: 'Captain Up' },
      // Round 11 item 4: these two were top-level modules (PR-C12) and are back
      // where Play has them — directly after Captain Up.
      { path: 'banners', navLabel: 'Banners Logged-out' },
      // The lobby catalog (RND-2259 / ADR-031) — Play lists Games directly after
      // Banners. ⚠ SKUs Library → Games (the offers slot catalog) is a DIFFERENT
      // page with the same label; only the active module expands, so the two
      // never render together.
      { path: 'games', navLabel: 'Games' },
      // The LogicX operational-log report (RND-2260) — Play's position, between
      // Games and Outbound Chat (routes.tsx:47-49). No `requiredRoles`: Play's
      // route carries no `support` flag; the module's operator gate applies, and
      // popcorn serves the read at viewer tier like every Marketing read.
      { path: 'logicx-logs', navLabel: 'LogicX Logs' },
      { path: 'outbound-chat', navLabel: 'Outbound Chat' },
      { path: 'dynamic-templates', navLabel: 'Dynamic Templates' },
      { path: 'step-presets', navLabel: 'Step Presets' },
    ],
    component: lazy(() => import('../marketing/MarketingModule')),
  },
  {
    // Play's `Apps` top-level menu (sc-admin MenuRegistrar.php:30-38,
    // dashicons-smartphone, position 25 — i.e. immediately before Leaderboard,
    // which is why it sits here). See the ADR-028 note above.
    //
    // VIEWER mount, unlike Play's single `manage-promos` cap: the page is
    // read-heavy and its writes gate separately inside it — instance CRUD needs
    // `operator`, and the three per-app checkboxes need `admin` because they
    // write `platform.feature_configs`, whose own PUT is admin-tier. Widening
    // that gate from here would be a side door into the feature switch.
    path: '/apps',
    routePattern: '/apps/*',
    navLabel: 'Apps',
    icon: Smartphone, // Play: dashicons-smartphone
    requiredRoles: ['viewer'],
    // No children: Play registers no submenu under Apps.
    component: lazy(() => import('../apps/AppsModule')),
  },
  {
    // The leaderboard-as-marketing pages (B8) promoted out of the Marketing tabs
    // into their own mount (cutover PR-C12). NOTE: the "Scheduler/Leaderboard are
    // NOT generic nav" doc-comment above refers to the OLD Play modules — this is
    // the tournament-domain authoring UI, not a resurrection of those.
    path: '/leaderboards',
    routePattern: '/leaderboards/*',
    // SINGULAR, matching Play (sc-admin/src/MenuRegistrar.php:40-48 — the string
    // "Leaderboards" appears nowhere in Play). The PATH stays plural: renaming it
    // would break every existing deep link for a cosmetic gain.
    navLabel: 'Leaderboard',
    icon: BarChart3, // Play: dashicons-chart-bar
    requiredRoles: ['operator'],
    // Play's submenu is `Create / Edit · Reports · Templates`. DELIBERATE
    // divergence: "Create / Edit" names a LIST page. Recorded in
    // docs/qa/marketing-parity-checklist.md as a chosen divergence.
    //
    // ⚠ `Schedules` was REMOVED here (ADR-028). It existed because Play's
    // scheduler had no gigantica counterpart, so its one-rule-per-template
    // reshape was parked under Leaderboard. The real `Scheduler` mount below
    // replaces it; keeping both would give the same recurrence two editors.
    children: [
      { path: '', navLabel: 'Instances' },
      { path: 'templates', navLabel: 'Templates' },
      { path: 'reports', navLabel: 'Reports' },
    ],
    component: lazy(() => import('../leaderboards/LeaderboardsModule')),
  },
  {
    // Play's `Scheduler` top-level menu (sc-admin MenuRegistrar.php:91-99,
    // dashicons-calendar-alt, position 27 — immediately after Leaderboard).
    // Page title is "Scheduler"; "App Schedules" is the h2 on the list card.
    //
    // OPERATOR mount, unlike Apps: every action here (create/edit/toggle/delete/
    // run-now) is operator-gated, so a viewer would see a page of dead controls.
    path: '/scheduler',
    routePattern: '/scheduler/*',
    navLabel: 'Scheduler',
    icon: CalendarDays, // Play: dashicons-calendar-alt
    requiredRoles: ['operator'],
    // No children: Play registers no submenu under Scheduler.
    component: lazy(() => import('../scheduler/SchedulerModule')),
  },
  {
    path: '/media-library',
    // ⚠ REQUIRED, not optional: this module has `children`, so it owns sub-paths and
    // `App.tsx` (which matches on `routePattern ?? path`) needs the wildcard. Deleting
    // this line takes the whole module offline — see the identical warning on `/platform`.
    routePattern: '/media-library/*',
    // Play's menu is WP core's "Media" (upload.php, dashicons-admin-media).
    navLabel: 'Media',
    icon: ImageIcon,
    requiredRoles: ['operator'],
    // WP's Media menu is exactly this pair: Library (upload.php) + Add Media File
    // (media-new.php). Both are now real screens, so the rail mirrors it.
    children: [
      { path: '', navLabel: 'Library' },
      { path: 'new', navLabel: 'Add Media File' },
    ],
    component: lazy(() => import('../media-library/MediaLibraryModule')),
  },
  {
    // admin-platform-module B5: Test Users · Meta · Sync Reports. One mount with
    // internal sub-routes (deep-linkable), so it needs a wildcard routePattern.
    // ABSORBS the former `/sync` "Sync Management" placeholder (Sync Reports is the
    // B6 read-only view inside this module now).
    path: '/platform',
    // ⚠ REQUIRED, not optional: this module has `children`, so it owns sub-paths and
    // `App.tsx` (which matches on `routePattern ?? path`) needs the wildcard. Deleting
    // this line takes the whole module offline — the index redirect lands on a child
    // path that no longer matches and every click renders "Not found". A label rename
    // dropped it once; `SideNav.test.tsx` now gates it.
    routePattern: '/platform/*',
    // Play's menu title is "Admin" (its PAGE title is "Admin Tools" —
    // sc-admin/src/MenuRegistrar.php:101-109 registers them differently). The PATH
    // stays `/platform`: renaming it would break deep links for a label change.
    navLabel: 'Admin',
    icon: Wrench, // Play: dashicons-admin-tools (a wrench/spanner)
    requiredRoles: ['viewer'],
    children: [
      { path: 'test-users', navLabel: 'Test Users' },
      // Play's own menu title for this tool is "Meta Editor"
      // (AdminToolsPage.php:56). The PATH stays `meta` — a label change must not break
      // deep links, same rule as the parent's `/platform`.
      { path: 'meta', navLabel: 'Meta Editor' },
      { path: 'maintenance', navLabel: 'Maintenance' },
      { path: 'sync-reports', navLabel: 'Sync Reports' },
      // RND-2258. Play has this tool too (its own Admin submenu item), so the
      // label matches; the PATH is gigantica's own — Play's is a WordPress
      // `admin.php?page=` query, which has no equivalent here.
      { path: 'geo-access-codes', navLabel: 'Geo Access Codes' },
      // ADR-024 amendment (f): purchase transactions and the LogicX settlement
      // calls behind them. OPERATOR, narrower than the module's viewer tier —
      // the rows carry customer ids, amounts and raw LogicX bodies. This only
      // hides the rail link; `PlatformModule` gates the routes themselves.
      { path: 'payments', navLabel: 'Payments', requiredRoles: ['operator'] },
      {
        path: 'payment-webhook-calls',
        navLabel: 'Payment Webhook Calls',
        requiredRoles: ['operator'],
      },
    ],
    component: lazy(() => import('../platform/PlatformModule')),
  },
  // ⚠ No `User Management` mount, ON PURPOSE (operator decision 2026-08-28): admin
  // users and their roles are managed in Keycloak (the §10 open question resolved
  // against a self-service surface). The former `/users` placeholder module was
  // removed with its `modules/` folder — do not re-add a stub for it.
  {
    // Segmentation-service traits + segments (NOT the popcorn marketing
    // segments — hence the plain "Marketing" label above naming Popcorn pages
    // individually). Viewer mount; per-action write gating (operator) lives
    // inside the pages.
    path: '/segmentation',
    routePattern: '/segmentation/*',
    // The "New Offers" band (2026-09-26, operator request): Segmentation Lab
    // (offer targeting), SKUs Library and Campaigns are where
    // gigantica configures offers — areas Play's wp-admin never had — the way
    // "New Shop" marks the Shop System below. One heading renders, above this
    // first module.
    section: 'New Offers',
    // Renamed from 'Segmentation' (2026-08-25) with the Lab rework — the label
    // only; the PATH stays, because renaming /segmentation would break every
    // deep link for a cosmetic gain (the same reasoning as the round-11
    // 'Leaderboard' rename).
    navLabel: 'Segmentation Lab',
    icon: Layers,
    requiredRoles: ['viewer'],
    // Segments FIRST (2026-08-31, operator request): segments are the Lab's
    // product, traits their building blocks — the module's index redirect
    // (SegmentationModule.tsx) moved to `segments` in the same change, so the
    // rail order and the landing page agree.
    children: [
      { path: 'segments', navLabel: 'Segments' },
      { path: 'traits', navLabel: 'Traits' },
    ],
    component: lazy(() => import('../segmentation/SegmentationModule')),
  },
  {
    // The SKU catalog both sales channels sell from — offer groups (Campaigns)
    // and shops (ADR-040) — plus the reference data an SKU's grant resolves
    // against. "Games" here is the SLOT-GAME catalog offer free-spins configs
    // reference (offers.games) — NOT the Captain Up mission list in
    // config/captainup-games.json that Marketing's message templates use.
    // The old `/offers/<section>` catalog URLs redirect here (`OffersModule`).
    path: '/skus',
    routePattern: '/skus/*',
    section: 'New Offers',
    navLabel: 'SKUs Library',
    icon: Boxes,
    requiredRoles: ['viewer'],
    children: [
      { path: '', navLabel: 'All SKUs' },
      { path: 'games', navLabel: 'Games' },
      // The CoreX reward templates (sweepstakes + free spins) offers resolve
      // their grant against, as entities (2026-09-10): imported from Magic X or
      // created by hand.
      { path: 'sweepstakes-templates', navLabel: 'Sweepstakes Templates' },
      { path: 'free-spins-templates', navLabel: 'Free Spins Templates' },
    ],
    component: lazy(() => import('../skus/SkusModule')),
  },
  {
    // Offer-system campaigns: the Orchestrator list and each campaign's
    // workspace, where its offer groups are configured. The SKU catalog moved
    // to its own module above; `/offers/groups*` and the old catalog URLs
    // still resolve through redirect routes in `OffersModule`.
    path: '/offers',
    // Still required without children: the campaign pages are sub-paths.
    routePattern: '/offers/*',
    section: 'New Offers',
    // Renamed 'Offers' → 'Campaigns' (2026-08-31, operator request) — the
    // campaign became the configuration hub, so the module is named after it.
    // LABELS only; the /offers PATH stays, same deep-link reasoning as the
    // 'Segmentation Lab' rename above.
    navLabel: 'Campaigns',
    icon: Ticket,
    requiredRoles: ['viewer'],
    // `campaigns`, not '': the index only redirects there, so '' would never light up.
    // `priorities` is the ADR-045 offer-group priority dictionary.
    children: [
      { path: 'campaigns', navLabel: 'All campaigns' },
      { path: 'priorities', navLabel: 'Priorities' },
    ],
    component: lazy(() => import('../offers/OffersModule')),
  },
  {
    // Shop System (SHOP 5.0): LiveOps storefronts — priority-ordered sets of
    // Offer-Library SKUs targeted by segment, released via Draft→Publish. The
    // per-shop editor tabs (general/packs/segments/preview) live INSIDE the
    // editor route, not here — rail children are static module sections.
    path: '/shops',
    routePattern: '/shops/*',
    section: 'New Shop',
    navLabel: 'Shops',
    icon: Store,
    requiredRoles: ['viewer'],
    // The whole Shop System hangs off this ONE module: the list, its settings
    // (also linked from the list's toolbar) and the three visual/history sections.
    children: [
      { path: '', navLabel: 'All shops' },
      { path: 'settings', navLabel: 'Settings' },
      { path: 'artbook-grid', navLabel: 'Artbooks & Grids', note: 'PH2' },
      { path: 'preview', navLabel: 'Shop Preview', note: 'PH2' },
      { path: 'audit-log', navLabel: 'Shop Audit Log', note: 'PH2' },
    ],
    component: lazy(() => import('../shops/ShopsModule')),
  },
  {
    // Visual Twig templates (NOT the popcorn dynamic templates) — a shared
    // system (offers and offer groups attach it today, rewards next; ADR-042),
    // so a band of its own after its consumers, not a "New Offers" module.
    path: '/templates',
    routePattern: '/templates/*',
    section: 'Templates',
    navLabel: 'Templates',
    icon: LayoutTemplate,
    requiredRoles: ['viewer'],
    // The index child's label differs from the module's (as `Library` /
    // `Instances` do) so the two rows never read as a duplicate. `Fonts` is the
    // uploaded-font registry the template editor's font picker offers, `Text
    // styles` the named text looks it applies; both stay at the module's viewer
    // tier because their `GET`s are viewer-scope — Fonts hides upload/delete
    // below admin, Text styles is read-only below operator (delete/import admin).
    children: [
      { path: '', navLabel: 'All templates' },
      { path: 'fonts', navLabel: 'Fonts' },
      { path: 'text-styles', navLabel: 'Text styles' },
    ],
    component: lazy(() => import('../templates/TemplatesModule')),
  },
];
